// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import axios from 'axios';
import { FileBrowser, FileNavbar, FileToolbar, FileList, FileContextMenu, ChonkyActions, FileActionData, FileArray, FileData, defineFileAction } from 'chonky';
import { ChonkyIconFA } from 'chonky-icon-fontawesome';

import { BaseModal } from '@shared/components/BaseModal';

interface FileInfo {
  key: string;
  name: string;
  size: number;
  lastModified: string | null;
  url: string;
}

interface FileManagerModalProps {
  open: boolean;
  gameKeyId: string;
  gameKey: string;
  onClose: () => void;
}

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const isImage = (name: string) => /\.(png|jpe?g|gif|webp|svg)$/i.test(name);
const isAudio = (name: string) => /\.(mp3|wav|ogg)$/i.test(name);

const FileManagerModal: React.FC<FileManagerModalProps> = ({ open, gameKeyId, gameKey, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<FileInfo[]>([]);
  const dropRef = useRef<HTMLDivElement | null>(null);
  const [imageDims, setImageDims] = useState<Record<string, { w: number; h: number; ratio: string }>>({});

  const chonkyFiles: FileArray = useMemo(() => {
    return files.filter(Boolean).map((f) => {
      const dims = imageDims[f.key];
      const sizeLabel = Number.isFinite(f.size) ? formatBytes(f.size) : '';
      const details = [
        dims ? `${dims.w}x${dims.h}` : null,
        dims ? dims.ratio : null,
        sizeLabel || null,
      ].filter(Boolean).join(', ');
      const prettyName = details ? `${f.name} (${details})` : f.name;
      const data: FileData = {
        id: f.key,
        name: prettyName,
        isDir: false,
        size: Number.isFinite(f.size) ? f.size : 0,
        modDate: f.lastModified ? new Date(f.lastModified) : undefined,
      } as any;
      if (isImage(f.name)) {
        (data as any).thumbnailUrl = f.url;
      }
      return data;
    });
  }, [files, imageDims]);

  const totalSizeLabel = useMemo(() => {
    const total = files.reduce((acc, f) => acc + (Number.isFinite(f.size) ? f.size : 0), 0);
    return formatBytes(total);
  }, [files]);

  const THUMB_SIZE = 220;
  const TILE_WIDTH = THUMB_SIZE + 60; // место для паддингов/имени
  const TILE_HEIGHT = THUMB_SIZE + 90; // место для подписи

  const fetchFiles = async () => {
    if (!open || !gameKeyId) return;
    try {
      setLoading(true);
      const res = await axios.get(`/admin/games/keys/${gameKeyId}/files`, { withCredentials: true });
      setFiles(res.data.files || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFiles();
    }
  }, [open, gameKeyId]);

  // Подгружаем размеры изображений
  useEffect(() => {
    if (!files.length) return;
    const next: Record<string, { w: number; h: number; ratio: string }> = {};
    const jobs: Array<Promise<void>> = [];
    files.forEach((f) => {
      if (isImage(f.name) && f.url && !imageDims[f.key]) {
        jobs.push(new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const w = img.naturalWidth || img.width;
            const h = img.naturalHeight || img.height;
            const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
            const g = gcd(w, h) || 1;
            const ratio = `${Math.round(w / g)}:${Math.round(h / g)}`;
            next[f.key] = { w, h, ratio };
            resolve();
          };
          img.onerror = () => resolve();
          img.src = f.url;
        }));
      }
    });
    if (!jobs.length) return;
    Promise.all(jobs).then(() => {
      if (Object.keys(next).length) setImageDims((prev) => ({ ...prev, ...next }));
    });
  }, [files]);

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const prevent = (e: DragEvent) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files);
      }
    };
    el.addEventListener('dragenter', prevent as any);
    el.addEventListener('dragover', prevent as any);
    el.addEventListener('drop', onDrop as any);
    return () => {
      el.removeEventListener('dragenter', prevent as any);
      el.removeEventListener('dragover', prevent as any);
      el.removeEventListener('drop', onDrop as any);
    };
  }, [dropRef.current, gameKeyId]);

  const handleUpload = async (fileList: FileList) => {
    if (!fileList || fileList.length === 0) return;
    const form = new FormData();
    Array.from(fileList).forEach(f => form.append('files', f, f.name));
    try {
      await axios.post(`/admin/games/keys/${gameKeyId}/files`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchFiles();
    } finally {
    }
  };

  const handleDelete = async (keys: string[]) => {
    if (!keys.length) return;
    await axios.delete(`/admin/games/keys/${gameKeyId}/files`, {
      withCredentials: true,
      data: { keys },
    });
    await fetchFiles();
  };

  const handleRename = async (oldKey: string, newName: string) => {
    if (!oldKey || !newName) return;
    await axios.post(`/admin/games/keys/${gameKeyId}/files/rename`, {
      oldKey,
      newName,
    }, { withCredentials: true });
    await fetchFiles();
  };

  // Кастомные действия принудительно в тулбаре/контекстном меню
  const ActionRename = defineFileAction({
    id: 'custom-rename',
    requiresSelection: true,
    button: { name: 'Rename', toolbar: true, contextMenu: true },
    __payloadType: undefined,
  });
  const ActionDelete = defineFileAction({
    id: 'custom-delete',
    requiresSelection: true,
    button: { name: 'Delete', toolbar: true, contextMenu: true },
    __payloadType: undefined,
  });
  const ActionDownload = defineFileAction({
    id: 'custom-download',
    requiresSelection: true,
    button: { name: 'Download', toolbar: true, contextMenu: true },
    __payloadType: undefined,
  });

  const onFileAction = async (data: FileActionData<any>) => {
    const { id, payload } = data;
    const selected = (data.state?.selectedFilesForAction || data.state?.selectedFiles || []) as FileData[];
    try {
      const filesFromPayload: FileData[] = (payload?.files && (payload.files as any[]).length)
        ? payload.files as any
        : ((payload?.targetFile ? [payload.targetFile as any] : []) as FileData[]);
      const filesEffective = filesFromPayload.length ? filesFromPayload : selected;

      if (id === ChonkyActions.OpenFiles.id || id === ActionDownload.id || id === ChonkyActions.DownloadFiles.id) {
        filesEffective.forEach((f: FileData) => {
          const found = files.find((x) => x.key === f.id);
          if (found?.url) window.open(found.url, '_blank');
        });
      }
      if (id === ChonkyActions.UploadFiles.id) {
        const fileList = payload?.files as FileList | undefined;
        if (fileList) await handleUpload(fileList);
      }
      if (id === ChonkyActions.DeleteFiles.id || id === ActionDelete.id) {
        const toDelete = filesEffective.map((f: FileData) => String(f.id));
        await handleDelete(toDelete);
      }
      if (id === ChonkyActions.RenameFiles.id || id === ActionRename.id) {
        const file = (payload?.file as FileData | undefined) || filesEffective[0];
        const newName = payload?.newName as string | undefined;
        if (file && newName) {
          await handleRename(String(file.id), newName);
        } else if (file) {
          const current = String(file.name || '');
          const base = current.replace(/\.[^.]+$/, '');
          const next = prompt('Новое имя файла:', base);
          if (next && next.trim()) await handleRename(String(file.id), next.trim());
        }
      }
      if (id === ChonkyActions.DownloadFiles.id) {
        const toDownload = filesFromPayload.map((f: FileData) => String(f.id));
        toDownload.forEach((k: string) => {
          const found = files.find((x) => x.key === k);
          if (found) window.open(found.url, '_blank');
        });
      }
    } catch (e) {
      // noop
    }
  };

  return (
    <BaseModal
      headerTitle={`Файлы: ${gameKey}`}
      isOpened={open}
      onSubmit={() => {}}
      handleClose={() => { onClose(); }}
      buttonConfirmedText=""
      hideSubmitButton
      width={900}
    >
      <Box sx={{ height: 'calc(94vh - 140px)', width: '100%' }} ref={dropRef} onDoubleClickCapture={(e) => { e.stopPropagation(); }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5 }}>
          <Box sx={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }}>
            {files.length} items • {totalSizeLabel}
          </Box>
        </Box>
        <style>{`
          .chonky-grid .chonky-file-entry { width: ${TILE_WIDTH}px !important; height: ${TILE_HEIGHT}px !important; }
          .chonky-grid .chonky-file-entry-preview { width: ${THUMB_SIZE}px !important; height: ${THUMB_SIZE}px !important; }
          .chonky-grid .chonky-file-entry-preview img { object-fit: contain; }
          .chonky-grid .chonky-file-entry-name { white-space: normal !important; line-height: 1.2; font-size: 12px; max-height: 60px; overflow: hidden; }
        `}</style>
        <FileBrowser
          files={chonkyFiles}
          folderChain={[{ id: 'root', name: gameKey, isDir: true }] as any}
          onFileAction={onFileAction}
          iconComponent={ChonkyIconFA}
          disableDragAndDropProvider={false}
          disableKeyboardShortcuts={false}
          clearSelectionOnOutsideClick
          defaultFileViewActionId={ChonkyActions.EnableGridView.id}
          fileActions={[
            ChonkyActions.OpenFiles,
            ChonkyActions.UploadFiles,
            ChonkyActions.DownloadFiles,
            ChonkyActions.EnableListView,
            ChonkyActions.EnableGridView,
            ChonkyActions.SelectAllFiles,
            ChonkyActions.ClearSelection,
            ActionRename,
            ActionDelete,
            ActionDownload,
          ]}
        >
          <FileNavbar />
          <FileToolbar />
          <FileList />
          <FileContextMenu anchorMode="mouse" />
        </FileBrowser>
      </Box>
    </BaseModal>
  );
};

export default FileManagerModal;


